import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import tinycolor from "tinycolor2";
import * as XLSX from "xlsx";
import isEmpty from "lodash/isEmpty";
import { APP_ID, TimeOnlyZone, TimeZoneIndia } from "./constants";

export function toastError(error) {
  toast.error(getErrorResponse(error));
}
export function getErrorResponse(error) {
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  } else {
    return "An error occurred. Please try again.";
  }
}
export const getDbHeader = () => {
  return {
    headers: {
      "content-type": "application/json",
      dbauth: APP_ID, //db: testapp
    },
  };
};
export const saveUser = (role, data) => {
  localStorage.setItem(role, JSON.stringify(data));
};
export const getUser = (role) => {
  return JSON.parse(localStorage.getItem(role));
};
export const deleteUser = (role) => {
  localStorage.removeItem(role);
};
export const isUserLoggedIn = (role) => {
  return localStorage.getItem(role) !== null;
};
export function navToElementMarker(elem, msg) {
  if (elem.current) {
    elem.current.scrollIntoView({ behavior: "smooth", block: "center" });
    elem.current.parentElement.style.border = "1px solid red";
    setTimeout(() => {
      elem.current.parentElement.style.border = ""; // Revert to the original style
    }, 3000);
    toast.error(msg);
  }
}
export const UserAuth = ({ children, type }) => {
  return isUserLoggedIn(type) ? children : <Navigate to="/login" />;
};
export const formatDateMMMddYYYY = (dateString) => {
  const date = new Date(dateString);
  const options = { year: "numeric", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-GB", options).replace(",", "");
};
export const getHeader = (type) => {
  return {
    headers: {
      "content-type": "application/json",
      token: getUser(type)?.token,
    },
  };
};
export const getMultipartHeader = (type) => {
  return {
    headers: {
      "Content-Type": "multipart/form-data",
      token: getUser(type)?.token,
    },
  };
};
export function useQuery() {
  return new URLSearchParams(useLocation().search);
}
export function getHoverColor(baseColor) {
  const hoverColor = tinycolor(baseColor).brighten(10).toString();
  return hoverColor;
}
export function getContrastingTextColor(hexColor) {
  const color = new tinycolor(hexColor);
  return color.isLight() ? "black" : "white";
}
export function hexToRGBA(hex, alpha = 1) {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
export function formatMongoDate(isoDateString) {
  const date = new Date(isoDateString);
  const day = date.getUTCDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getUTCMonth()];
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 24-hour format to 12-hour format
  return `${day} ${month} ${hours}:${minutes}${ampm}`;
}
export function extractTime(isoString) {
  if (isEmpty(isoString)) {
    return "";
  }
  const dateObj = new Date(isoString);
  return dateObj.toLocaleTimeString("en-IN", TimeOnlyZone);
}
export function extractDate(isoString) {
  if (isEmpty(isoString)) {
    return "";
  }
  const dateObj = new Date(isoString);
  return dateObj.toLocaleDateString("en-IN", TimeZoneIndia); // en-GB format is dd/mm/yyyy
}
export function roundToTwoDecimals(num) {
  return parseFloat(num.toFixed(2));
}

export const exportToExcel = (data, fileName) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};
export const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export function formatVisitorForExcel(item) {
  if (!item) return {};

  // Extract selected product categories array
  const rawProductCats =
    item.selectProductCategoriesChooseMin10Products ||
    item.selectProductCategories ||
    item.productCategories ||
    item.selectedCategories ||
    item.productCategory ||
    item["Select Product Categories"] ||
    item["Select Product Categories (Choose Min 10 Products)"] ||
    item["selectProductCategoriesChooseMin10Products"] ||
    item["Selected Product Categories"] ||
    [];

  let categoryList = [];
  if (Array.isArray(rawProductCats)) {
    categoryList = rawProductCats;
  } else if (Array.isArray(rawProductCats?.value)) {
    categoryList = rawProductCats.value;
  } else if (typeof rawProductCats === "string" && rawProductCats.trim()) {
    try {
      const parsed = JSON.parse(rawProductCats);
      if (Array.isArray(parsed)) {
        categoryList = parsed;
      } else {
        categoryList = [rawProductCats];
      }
    } catch {
      categoryList = [rawProductCats];
    }
  }

  const formattedProductCats = categoryList
    .map((p) => {
      if (typeof p === "string") return p;
      if (p && typeof p === "object") {
        const parts = [
          p.MainCategory || p.mainCat,
          p.Category || p.subCat,
          p.SubCategory || p.subSubCat,
        ].filter(Boolean);
        if (parts.length > 0) return parts.join(" > ");
        if (p.code && p.name) return `${p.code} - ${p.name}`;
        return p.name || p.label || p.title || p.code || "";
      }
      return "";
    })
    .filter(Boolean)
    .join(" | ");

  const title = item.title || "";
  const firstName = item.firstName || item.contactFirstName?.value || "";
  const lastName = item.lastName || item.contactLastName?.value || "";
  const fullName = `${title} ${firstName} ${lastName}`.replace(/\s+/g, " ").trim();

  const countryName =
    item.country?.name ||
    item.countryName ||
    (typeof item.country === "string" ? item.country : "");
  const countryCode = item.country?.code || "";

  const stateName =
    item.state?.name ||
    item.stateName ||
    (typeof item.state === "string" ? item.state : "");
  const stateCode = item.state?.code || "";

  const cityName =
    item.city?.name ||
    item.cityName ||
    (typeof item.city === "string" ? item.city : "");

  return {
    "Object ID": item._id || "",
    "User ID": item.user_id || "",
    "Barcode ID": item.barcode_id || "",
    Username: item.username || "",
    Title: title,
    "First Name": firstName,
    "Last Name": lastName,
    "Full Name": fullName,
    Designation: item.designation || item.contactDesignation?.value || item.role || item.jobTitle || "",
    "Mobile No": item.mobileNo || item.mobile || item.phone?.value || item.phone || item.phoneNo || item.contactNo || "",
    Email: item.email || item.contactEmail?.value || "",
    "Company Name": (typeof item.companyName === "string" ? item.companyName : item.companyName?.value) || item.company || item.institute || item.institution || item.institutionName || item.organization || "",
    Address: item.address || "",
    Country: countryName,
    "Country Code": countryCode,
    State: stateName,
    "State Code": stateCode,
    City: cityName,
    "Pin/Zip": item.pinzip || item.pin || item.zip || "",
    "Industry Sector": item.industrySector || "",
    Department: item.department || "",
    "How Did You Find Out About Event":
      item.howDidYouFindOutAboutThisEvent || "",
    "Selected Product Categories": formattedProductCats,
    "Event Visitor Type": item.eventVisitor || "",
    "QR Link": item.qrLink || "",
    DB: item.db || "",
    Role: item.role || "",
    Template: item.template || "",
    "Created On": extractDate(item.createdOn),
    "Updated On": extractDate(item.updatedOn),
  };
}
